import { Tooltip } from 'antd';
import { useMemo } from 'react';
import {
  MINUTES_IN_DAY,
  coverageOf,
  coverageState,
  describeCoverage,
  formatTimeOfDay,
  type CoverageShift,
} from '@/lib/shiftCoverage';
import { TONE_VAR } from '@/theme/statusTone';
import { cn } from '@/lib/utils';

/**
 * ─── LAYER: component (shared intake) ────────────────────────────────────────────
 *
 * THE 24-HOUR COVERAGE BAR. It fills as shifts are added, and shows where nobody is on
 * duty. "Round the clock" with a two-hour hole at 04:00 is invisible until it is a real
 * hole on a real night.
 *
 * ⚠️ IT WARNS. IT NEVER BLOCKS. There is no `role="alert"`, no `aria-invalid`, and nothing
 * it returns reaches a Save button. An operator may have a reason for a gap or a handover
 * overlap, and a form that blocks with no override is how people go back to keeping the
 * real roster in a spreadsheet.
 *
 * ⚠️ IT COMPUTES ITS OWN COVERAGE from the shifts, so no caller can hand it one that
 * disagrees with what is on screen.
 *
 * ⚠️ THE BAR IS `aria-hidden` AND THE SENTENCE IS THE CONTENT.
 *
 * The tempting alternative — `role="img"` with the description as its label — puts the
 * information somewhere a SIGHTED COLOUR-BLIND OPERATOR never gets it, and that is the
 * exact user the rule exists for. The same reasoning makes `StatusDot`'s text label
 * required rather than optional. Every band also carries `data-kind` and a `title`, so the
 * state is readable by a test, by a pointer and by a screen reader without once depending
 * on hue.
 */

export type CoverageBarProps = {
  shifts: readonly CoverageShift[];
  /** One sentence of guidance. A PROP, because `components/` must not reach into a page's copy. */
  hint?: string;
  className?: string;
  'data-testid'?: string;
};

/**
 * ⚠️ TEXTURE AS WELL AS HUE, AND NOT ONE NEW COLOUR. The three treatments differ in
 * busyness and in hatch DIRECTION, so gap / covered / overlap stay separable in greyscale.
 * `color-mix` over a tone variable is `StatusPill`'s idiom; the hatch is
 * `HierarchyTable`'s. `theme/tokens.ts` remains the only file in the repo with a colour.
 */
const FILL = {
  gap: {
    background: `repeating-linear-gradient(45deg, color-mix(in srgb, ${TONE_VAR.warning} 55%, transparent) 0 2px, transparent 2px 6px), color-mix(in srgb, ${TONE_VAR.warning} 18%, transparent)`,
    label: 'nobody on duty',
  },
  covered: {
    background: `color-mix(in srgb, ${TONE_VAR.success} 45%, transparent)`,
    label: 'covered',
  },
  overlap: {
    background: `repeating-linear-gradient(135deg, color-mix(in srgb, ${TONE_VAR.info} 60%, transparent) 0 2px, transparent 2px 5px), color-mix(in srgb, ${TONE_VAR.info} 30%, transparent)`,
    label: 'covered twice over',
  },
} as const;

const kindOf = (depth: number): keyof typeof FILL => (depth === 0 ? 'gap' : depth === 1 ? 'covered' : 'overlap');

export const CoverageBar = ({ shifts, hint, className, 'data-testid': testId = 'coverage-bar' }: CoverageBarProps) => {
  const coverage = useMemo(() => coverageOf(shifts), [shifts]);
  const state = coverageState(coverage);
  const kinds = new Set(coverage.segments.map((segment) => kindOf(segment.depth)));

  return (
    <section
      data-testid={testId}
      data-state={state}
      aria-labelledby="coverage-heading"
      className={cn('flex flex-col gap-2 rounded-lg border border-border bg-card p-3', className)}
    >
      <div className="flex items-center justify-between gap-2">
        <h3 id="coverage-heading" className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
          24-hour coverage
        </h3>
        {hint !== undefined && (
          <Tooltip title={hint}>
            <span className="text-xs text-muted-foreground" data-testid="coverage-hint">
              What is this?
            </span>
          </Tooltip>
        )}
      </div>

      {/* Decorative. Everything it says is said again in the sentence beneath it. */}
      <div className="relative h-6 w-full overflow-hidden rounded-md border border-border bg-secondary" aria-hidden>
        {coverage.segments.map((segment) => (
          <div
            key={`${String(segment.startMinute)}-${String(segment.endMinute)}`}
            data-kind={kindOf(segment.depth)}
            data-depth={segment.depth}
            title={`${formatTimeOfDay(segment.startMinute)}–${formatTimeOfDay(segment.endMinute)} · ${FILL[kindOf(segment.depth)].label}`}
            className="absolute inset-y-0"
            style={{
              left: `${String((segment.startMinute / MINUTES_IN_DAY) * 100)}%`,
              width: `${String(((segment.endMinute - segment.startMinute) / MINUTES_IN_DAY) * 100)}%`,
              background: FILL[kindOf(segment.depth)].background,
            }}
          />
        ))}
      </div>

      <div className="flex justify-between text-[10px] text-muted-foreground tabular-nums" aria-hidden>
        <span>00</span>
        <span>06</span>
        <span>12</span>
        <span>18</span>
        <span>24</span>
      </div>

      {/* ⚠️ THE CONTENT. `aria-live`, so a reader hears the state change as times are typed. */}
      <p className="text-xs text-muted-foreground" aria-live="polite" data-testid="coverage-summary">
        {describeCoverage(coverage)}
      </p>

      {kinds.size > 1 && (
        <ul className="flex flex-wrap gap-3 text-xs text-muted-foreground" data-testid="coverage-legend">
          {(['covered', 'gap', 'overlap'] as const)
            .filter((kind) => kinds.has(kind))
            .map((kind) => (
              <li key={kind} className="flex items-center gap-1.5">
                <span
                  className="inline-block size-3 rounded-xs border border-border"
                  style={{ background: FILL[kind].background }}
                  aria-hidden
                />
                {FILL[kind].label}
              </li>
            ))}
        </ul>
      )}
    </section>
  );
};
